import { Link } from "react-router-dom";
import {
  Clock, Lock, ShieldCheck, Mail, ArrowUpRight, Info, Download,
  Package, Users, Stethoscope, FlaskConical, BadgeCheck, Sparkles, Smartphone, Watch,
  FileCheck, Wrench, Plug, Microscope,
} from "lucide-react";
import { SEO } from "@/components/SEO";
import { PAGE_SEO } from "@/lib/seoData";
import { SectionHeading, Reveal } from "@/components/Section";
import ZoomImage from "@/components/ZoomImage";
import { EXPLODE_VIDEO, CAD_OVERLAY, SAFE_BAND_URL } from "@/lib/assets";

const COMPLIANCE_GAP = [
  {
    Icon: Package,
    title: "Carrying the Product",
    body: "A prescribed cream or serum is easy to leave in a bathroom cabinet, a gym bag or a desk drawer. A product that isn't within reach at the right moment doesn't get applied, no matter how well it was chosen.",
  },
  {
    Icon: Clock,
    title: "Remembering the Moment",
    body: "Most regimens ask for a specific cadence: twice daily, after cleansing, before sun exposure. Outside a clinical setting, that schedule competes with work, travel and everything else in a patient's day.",
  },
];

const VALUE_COLUMNS = [
  {
    Icon: Users,
    title: "Patients",
    body: "A practical path from knowing what to apply to being able to apply it, without an extra bag, an extra step or one more thing to remember.",
  },
  {
    Icon: Stethoscope,
    title: "Physicians & Clinical Programs",
    body: "A way to test whether easier access changes how consistently patients follow a recommended regimen, with real usage data where patients consent to share it.",
  },
  {
    Icon: FlaskConical,
    title: "Skincare Manufacturers",
    body: "Insight into how a formulation is actually used once it leaves the office, information that's hard to get once a product goes home with a patient.",
  },
];

const MATRIX = [
  {
    procedure: "Medical Microneedling",
    vulnerability: "Open micro-channels for 24 to 48 hours, contamination risk from unwashed fingers.",
    concept: "On-wrist access to a clinician-recommended soothing formulation, dispensed into the palm for the patient to apply by hand, proposed as an easier-access alternative to a bottle kept in a bag or cabinet.",
  },
  {
    procedure: "Laser Resurfacing",
    vulnerability: "Thermal heat, redness, water loss and crusting during re-epithelialization.",
    concept: "Scheduled reminders paired with on-wrist access to a clinician-specified formulation, dispensed into the palm for manual application, aimed at supporting a more consistent moisture routine.",
  },
  {
    procedure: "Surgical Incision Care",
    vulnerability: "Infection risk and the need for a clean barrier near sutures.",
    concept: "On-wrist access to a clinician-specified formulation, dispensed into the palm for the patient to apply by hand away from the incision line, following clinician instructions. The most medically sensitive use case here, and the one that would need the most clinical and regulatory review before any deployment.",
  },
  {
    procedure: "Living Biome & Advanced Actives",
    vulnerability: "Degradation from air, light and repeated opening of the container.",
    concept: "Sealed, opaque reservoir intended to protect sensitive formulations between uses.",
  },
  {
    procedure: "Neuromodulators & Fillers",
    vulnerability: "Bruising, swelling, and low adoption of a daily aftercare routine.",
    concept: "Scheduled wrist-based reminders meant to help build a consistent aftercare habit.",
  },
];

// Published research, in different patient populations, that informed the
// design of the pilot proposed below. None of these studies tested
// WearSpray or any wearable dispenser; each summary is sourced to its DOI.
const RESEARCH_SOURCES = [
  {
    citeKey: "newman1998",
    citation: "Newman JP, Koch RJ, Goode RL (1998). Effects of closed dressings after full-face laser resurfacing. Archives of Otolaryngology–Head & Neck Surgery, 124(7), 751–757.",
    link: "https://doi.org/10.1001/archotol.124.7.751",
    finding: "In a prospective study of 40 post-laser-resurfacing patients, closed wound dressings were worn for a mean of only 2.2 days, and most patients were unwilling to continue past 48 hours, choosing to revert to simpler open-wound care rather than return to the clinic.",
    limitation: "Facial CO2 laser resurfacing patients (N=40), evaluating postoperative wound dressings, not a wearable dispenser or a topical reapplication schedule.",
  },
  {
    citeKey: "so2003",
    citation: "So K, Umraw N, Scott J, Campbell K, Musgrave MA, Cartotto RC (2003). Effects of enhanced patient education on compliance with silicone gel sheeting and burn scar outcome: a randomized prospective study. Journal of Burn Care & Rehabilitation, 24(6), 411–417.",
    link: "https://doi.org/10.1097/01.bcr.0000095516.98523.04",
    finding: "Outpatients given a detailed handout and video wore prescribed silicone gel sheeting for 21.8 hours a day on average, versus 10.1 hours a day with routine instructions alone, and the better-educated group also showed measurably better Vancouver Scar Scale outcomes at six months.",
    limitation: "Hypertrophic burn-scar outpatients, testing a patient-education intervention rather than a dispensing device; shows that adherence support can change both wear time and measured outcome in this population, not that WearSpray would do the same.",
  },
  {
    citeKey: "park2014",
    citation: "Park C, Kim G, Patel I, Chang J, Tan X (2014). Improving adherence to acne treatment: the emerging role of application software. Clinical, Cosmetic and Investigational Dermatology, 7, 275–284.",
    link: "https://doi.org/10.2147/CCID.S46051",
    finding: "Reviewing controlled studies of topical acne treatment, the authors found that simple daily text-message reminders alone did not significantly improve adherence (33.9% vs. 36.5%), while weekly, more substantive engagement did improve it.",
    limitation: "Topical acne medication, reminder software only, no access or dispensing component tested; a reason to test reminders paired with immediate access rather than assume reminders alone are sufficient.",
  },
];

const PARTNERSHIP_PATHS = [
  {
    Icon: Microscope,
    title: "Clinical Validation",
    body: "Run a structured pilot alongside your own patients or research program to help build real evidence for whether accessible delivery changes adherence.",
  },
  {
    Icon: FileCheck,
    title: "Licensing",
    body: "License the dispensing platform, or specific patented mechanisms within it, for use inside your own branded formulations or devices.",
  },
  {
    Icon: Wrench,
    title: "Product Development",
    body: "Co-develop a branded dispensing format built around your formulation, your packaging and your patients' routines.",
  },
  {
    Icon: Plug,
    title: "Technology Integration",
    body: "Plan an integration of WearSpray IQ's in-development reminder logic with your own app, loyalty program or patient portal.",
  },
];

export default function ClinicalRecovery() {
  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden" data-testid="clinical-page">
      <SEO {...PAGE_SEO["/clinical"]} path="/clinical" />

      {/* HERO */}
      <section className="relative border-b border-zinc-800 overflow-hidden" data-testid="clinical-hero">
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover opacity-30"
            style={{ filter: "brightness(1.1) saturate(1.05)" }}
          >
            <source src={EXPLODE_VIDEO} type="video/mp4" />
          </video>
          <img loading="lazy" src={CAD_OVERLAY} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-contain mix-blend-screen opacity-40 p-10 md:p-24" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/75 to-black" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 pt-28 pb-16 md:pt-36 md:pb-20">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.28em] uppercase text-accent block mb-7">
              Clinical Recovery & Aesthetics
            </span>
            <h1 className="font-heading font-light tracking-tight leading-[1.1] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
              The missing link between personalized skincare and patient adherence.
            </h1>
            <p className="font-body text-zinc-400 text-lg md:text-xl leading-relaxed mt-7 max-w-2xl">
              Dermatology and medical aesthetics are investing heavily in personalized
              formulations, treatment programs and physician relationships. The piece still
              missing: making sure patients keep using what's recommended once they leave the
              office.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div data-testid="clinical-hero-disclaimer" className="flex items-start gap-3 border border-accent/25 bg-accent/[0.05] rounded-xl p-5 mt-10 max-w-2xl">
              <Info size={16} className="text-accent shrink-0 mt-0.5" />
              <p className="font-body text-sm text-zinc-300 leading-relaxed">
                This page describes an early concept WearSpray is exploring with dermatology,
                medical aesthetics and skincare manufacturing partners. It is not a description of
                a cleared medical device, and WearSpray makes no claim that any adherence or
                clinical outcome improvement has been demonstrated.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <a
              href="/docs/WearSpray_Clinical_Recovery_Overview.pdf"
              download
              data-testid="clinical-hero-download-pdf"
              className="inline-flex items-center gap-2 font-body text-sm text-zinc-400 hover:text-accent transition-colors mt-6"
            >
              <Download size={14} /> Download the one-pager (PDF)
            </a>
          </Reveal>
        </div>
      </section>

      {/* THE COMPLIANCE GAP */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="clinical-compliance-gap-section">
        <SectionHeading
          overline="The Problem"
          title="Recommended isn't the same as applied."
          subtitle="A personalized formulation only works if someone actually keeps using it, and that's where most regimens quietly fall apart."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-14 max-w-3xl">
          {COMPLIANCE_GAP.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <div data-testid={`clinical-compliance-point-${i}`} className="h-full border border-zinc-800 bg-[#0d0d0d] p-7 rounded-xl">
                <c.Icon size={22} className="text-accent" />
                <h3 className="font-heading text-lg font-medium text-white mt-5">{c.title}</h3>
                <p className="font-body text-sm text-zinc-400 leading-relaxed mt-4">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* THE WEARSPRAY ECOSYSTEM */}
      <section className="border-t border-zinc-900 bg-[#0a0a0a]" data-testid="clinical-ecosystem-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeading
            overline="The Platform"
            title="One ecosystem, built in stages."
            subtitle="Some of this is patented and already in production. Some of it is still in development. We think it's important to be clear about which is which."
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-14">
            {/* AVAILABLE TODAY */}
            <Reveal>
              <div data-testid="clinical-ecosystem-available" className="h-full border border-accent/30 bg-accent/[0.04] rounded-xl p-7">
                <div className="inline-flex items-center gap-2 bg-accent text-black px-3 py-1 rounded-full">
                  <BadgeCheck size={13} />
                  <span className="font-mono text-[10px] tracking-[0.15em] uppercase font-bold">Available Today</span>
                </div>
                <h3 className="font-heading text-xl font-medium text-white mt-5">Safe Band Pro wrist dispenser</h3>
                <p className="font-body text-sm text-zinc-300 leading-relaxed mt-3">
                  A patented, refillable wrist-worn dispenser that puts a chosen formulation,
                  cream, serum, lotion or gel, one press away. It's in production today, not a
                  rendering or a future promise.
                </p>
                <div className="flex items-center gap-3 mt-6">
                  <Lock size={15} className="text-accent" />
                  <span className="font-mono text-xs tracking-[0.15em] uppercase text-zinc-400">Refillable reservoir, manufactured and shipping today.</span>
                </div>
                <div className="mt-6">
                  <ZoomImage
                    src="/prototype/clinical-exploded-cartridge.webp"
                    alt="Exploded view of the WearSpray reservoir, valve and refillable cap mechanism"
                    className="w-full max-h-[320px] object-contain rounded-lg"
                    caption="Patented gate and valve design, paired with the same removable cap mechanism this platform uses today"
                    testId="clinical-zoom-valve"
                  />
                </div>
              </div>
            </Reveal>

            {/* IN DEVELOPMENT */}
            <Reveal delay={0.1}>
              <div data-testid="clinical-ecosystem-development" className="h-full border border-zinc-800 bg-[#0d0d0d] rounded-xl p-7">
                <div className="inline-flex items-center gap-2 border border-zinc-700 text-zinc-300 px-3 py-1 rounded-full">
                  <Sparkles size={13} />
                  <span className="font-mono text-[10px] tracking-[0.15em] uppercase font-bold">In Development</span>
                </div>

                <div className="mt-6 flex items-start gap-4">
                  <Lock size={19} className="text-accent shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-heading text-base font-medium text-white">Factory-sealed clinical cartridge</h4>
                    <p className="font-body text-sm text-zinc-400 leading-relaxed mt-1.5">
                      A proposed packaging variant of the same reservoir mechanism: a
                      single-formulation, tamper-evident pod with no refill access, designed to
                      snap into the band and be swapped as a whole unit for clinical hygiene use.
                      The reservoir was engineered from day one to be removable, which is what
                      would make this possible on the same hardware platform, but it has not yet
                      been tooled, manufactured or reviewed for clinical use.
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex items-start gap-4">
                  <Sparkles size={19} className="text-accent shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-heading text-base font-medium text-white">WearSpray IQ</h4>
                    <p className="font-body text-sm text-zinc-400 leading-relaxed mt-1.5">
                      An intelligence layer in development to pair personalized guidance with
                      contextual reminders, when to apply, based on time since last use, activity
                      and other signals a patient chooses to share.
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex items-start gap-4">
                  <Smartphone size={19} className="text-accent shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-heading text-base font-medium text-white">Phone-mounted reservoir</h4>
                    <p className="font-body text-sm text-zinc-400 leading-relaxed mt-1.5">
                      A proposed reservoir format that rides along with a device patients already
                      carry everywhere.
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex items-start gap-4">
                  <Watch size={19} className="text-accent shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-heading text-base font-medium text-white">Attachable wearable pod</h4>
                    <p className="font-body text-sm text-zinc-400 leading-relaxed mt-1.5">
                      A proposed modular pod designed to attach to a watch band, bag strap or
                      other everyday accessory.
                    </p>
                  </div>
                </div>

                <p className="font-body text-xs text-zinc-600 leading-relaxed mt-7 border-t border-zinc-800 pt-5">
                  These are concepts and development directions, not shipping products.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* VALUE FOR EVERYONE INVOLVED */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="clinical-value-section">
        <SectionHeading overline="Why It Matters" title="A practical path from recommendation to action." />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
          {VALUE_COLUMNS.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div data-testid={`clinical-value-card-${i}`} className="h-full border border-zinc-800 bg-[#0d0d0d] p-7 rounded-xl">
                <v.Icon size={22} className="text-accent" />
                <h3 className="font-heading text-lg font-medium text-white mt-5">{v.title}</h3>
                <p className="font-body text-sm text-zinc-400 leading-relaxed mt-4">{v.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SUPPORTING AFTERCARE BEYOND THE CLINIC */}
      <section className="border-t border-zinc-900 bg-[#0a0a0a]" data-testid="clinical-problem-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeading
            overline="A Closer Look"
            title="Supporting Aftercare Beyond the Clinic."
            subtitle="The gap is sharpest right after a procedure, when correct aftercare matters most and a missed step is hardest to undo."
          />
          <Reveal delay={0.1}>
            <p className="font-body text-zinc-300 text-base md:text-lg leading-relaxed mt-10 max-w-3xl" data-testid="clinical-problem-paragraph">
              After some dermatologic and aesthetic procedures, patients are asked to follow a
              specific at-home application schedule using a formulation their clinician
              recommends. That schedule has to compete with the rest of a patient's day, work,
              travel, childcare, once they leave the office. WearSpray's proposed role is narrow:
              pair the clinician's reminder with immediate access to that same recommended
              formulation, and test whether the combination helps more applications happen on
              time. We have not yet measured whether it does.
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHY STUDY ACCESS AND ADHERENCE */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="clinical-research-foundation-section">
        <SectionHeading
          overline="Research Foundation"
          title="Why Study Access and Adherence?"
          subtitle="Three published studies, each in a different patient population, informed the design of the pilot proposed below. None of them tested WearSpray or a wearable dispenser."
        />
        <div className="grid grid-cols-1 gap-6 mt-14">
          {RESEARCH_SOURCES.map((s, i) => (
            <Reveal key={s.citeKey} delay={Math.min(i * 0.08, 0.24)}>
              <div data-testid={`clinical-research-source-${i}`} className="border border-zinc-800 bg-[#0d0d0d] p-7 rounded-xl">
                <a
                  href={s.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`clinical-research-source-link-${i}`}
                  className="group inline-flex items-start gap-2 font-heading text-base md:text-lg font-medium text-white hover:text-accent transition-colors"
                >
                  {s.citation}
                  <ArrowUpRight size={15} className="shrink-0 mt-1 opacity-60 group-hover:opacity-100 transition-opacity" />
                </a>
                <p className="font-body text-sm text-zinc-400 leading-relaxed mt-3">{s.finding}</p>
                <p className="font-body text-xs text-zinc-400 leading-relaxed mt-3 border-t border-zinc-800 pt-3">
                  <span className="text-zinc-300">Population &amp; limitation: </span>
                  {s.limitation}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="font-body text-sm text-zinc-500 leading-relaxed mt-10 max-w-3xl italic" data-testid="clinical-research-disclaimer">
            These studies do not establish that WearSpray improves adherence, healing, infection
            risk or cosmetic outcome for any procedure. Taken together, they establish that
            patients commonly stop following prescribed at-home wound-care routines well before
            clinicians intend, that better-supported patient education measurably changed how
            long a prescribed topical regimen was actually followed in one burn-scar population,
            and that simple reminders alone have not reliably improved adherence to a prescribed
            topical regimen in at least one dermatology population, part of why the pilot below
            proposes testing reminders paired with immediate access, not reminders alone.
          </p>
        </Reveal>
      </section>

      {/* POTENTIAL RESEARCH APPLICATIONS */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="clinical-matrix-section">
        <SectionHeading
          overline="Procedural Fit"
          title="Potential Research Applications."
          subtitle="How the concept maps against common aesthetic and dermatology procedures, shown for discussion purposes. Any regimen would remain under clinician direction, reminders are a prompt, not a replacement for prescribed instructions, and this is not a demonstrated or validated indication for any procedure."
        />
        <div className="border border-zinc-800 bg-[#0d0d0d] mt-14 rounded-xl overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-zinc-800 bg-[#121212]">
            <span className="col-span-3 font-mono text-xs tracking-[0.15em] uppercase text-zinc-500">Procedure</span>
            <span className="col-span-5 font-mono text-xs tracking-[0.15em] uppercase text-zinc-500">Key Vulnerability</span>
            <span className="col-span-4 font-mono text-xs tracking-[0.15em] uppercase text-zinc-500">WearSpray Concept</span>
          </div>
          {MATRIX.map((row, i) => (
            <Reveal key={row.procedure} delay={Math.min(i * 0.05, 0.3)}>
              <div data-testid={`clinical-matrix-row-${i}`} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-6 border-b border-zinc-800 last:border-b-0 hover:bg-[#141414] transition-colors">
                <span className="md:col-span-3 font-heading text-base text-white">{row.procedure}</span>
                <span className="md:col-span-5 font-body text-sm text-zinc-400 leading-snug">{row.vulnerability}</span>
                <span className="md:col-span-4 font-body text-sm text-accent leading-snug">{row.concept}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PROPOSED CLINICAL EVALUATION */}
      <section className="border-t border-zinc-900 bg-[#0a0a0a]" data-testid="clinical-evaluation-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeading overline="Evidence We're Proposing to Build" title="A pilot designed to test one variable." />
          <Reveal delay={0.1}>
            <p className="font-body text-zinc-300 text-base md:text-lg leading-relaxed mt-10">
              The question we want to test is narrow on purpose: does immediate, wearable access
              change how consistently someone follows a personalized reminder, compared with the
              same reminder paired with a product carried the conventional way?
            </p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-10">
            <Reveal delay={0.14}>
              <div data-testid="clinical-evaluation-group-a" className="h-full border border-zinc-800 bg-[#0d0d0d] p-6 rounded-xl">
                <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-500">Group A &middot; Control</span>
                <h3 className="font-heading text-lg font-medium text-white mt-2">Personalized reminders + conventional access</h3>
                <p className="font-body text-sm text-zinc-400 leading-relaxed mt-3">
                  The same contextual prompts and the same clinician-specified regimen, paired
                  with the same compatible formulation carried in a normal bottle or tube.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.18}>
              <div data-testid="clinical-evaluation-group-b" className="h-full border border-accent/30 bg-accent/[0.04] p-6 rounded-xl">
                <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-accent">Group B &middot; Experimental</span>
                <h3 className="font-heading text-lg font-medium text-white mt-2">Personalized reminders + WearSpray access</h3>
                <p className="font-body text-sm text-zinc-300 leading-relaxed mt-3">
                  The identical prompts and the same clinician-specified regimen, paired with the
                  same compatible formulation available instantly from the wrist.
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.22}>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mt-10 mb-4">Proposed Measures</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5" data-testid="clinical-pilot-measures">
              {[
                "Timely completion of scheduled applications",
                "Missed or delayed applications",
                "Convenience and usability",
                "Product consumption",
                "Patient experience",
                "Tolerability",
              ].map((m) => (
                <li key={m} className="font-body text-sm text-zinc-300 flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-accent shrink-0 mt-2" />
                  {m}
                </li>
              ))}
            </ul>
            <p className="font-body text-sm text-zinc-400 leading-relaxed mt-6">
              Dispensing events and reported application are not the same as independently
              confirmed application, the pilot design would need to measure and report that
              distinction rather than treat a dispense or a self-report as proof the formulation
              was actually applied.
            </p>
            <p className="font-body text-sm text-zinc-500 leading-relaxed mt-4 italic">
              This pilot has not been run. Partner outcomes described anywhere on this page are
              proposed, not established, and no clinical claim is implied by describing it.
            </p>
          </Reveal>

          <Reveal delay={0.26}>
            <Link
              to="/contact?topic=Clinical%20Collaboration"
              data-testid="clinical-pilot-cta"
              className="inline-flex items-center gap-2 font-body text-sm font-medium bg-accent text-black px-8 py-4 mt-10 hover:bg-white transition-colors"
            >
              <Mail size={16} /> Discuss a Clinical Pilot
            </Link>
          </Reveal>
        </div>
      </section>

      {/* PRACTICE ECONOMICS */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="clinical-economics-section">
        <SectionHeading overline="Practice Economics" title="Keeping refills with the practice." />
        <Reveal delay={0.1}>
          <p className="font-body text-zinc-300 text-base md:text-lg leading-relaxed mt-10">
            Practices commonly report losing a meaningful share of post-procedure topical
            reorders to third-party retailers once a patient leaves the building. The concept
            calls for an automated low-reservoir prompt that routes a refill back to the
            prescribing practice rather than a general marketplace, which could help with both
            patient retention and recapturing revenue otherwise lost to attrition. These are
            planning assumptions for discussion with practices, not measured outcomes from a
            deployed program.
          </p>
        </Reveal>
      </section>

      {/* VALIDATION */}
      <section className="border-t border-zinc-900 bg-[#0a0a0a]" data-testid="clinical-validation-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeading overline="Built on Issued Patents" title="Not a concept from scratch." />
          <Reveal delay={0.1}>
            <p className="font-body text-zinc-300 text-base md:text-lg leading-relaxed mt-10">
              This explores a new application of hardware that already exists.{" "}
              <Link to="/patents" data-testid="clinical-link-patents" className="text-accent hover:underline">U.S. Patent No. 12,285,084 B1</Link>, Claim
              13, covers the sensing and micro-fluidic dispensing architecture behind it, and the
              physical platform,{" "}
              <a href={SAFE_BAND_URL} target="_blank" rel="noopener noreferrer" data-testid="clinical-link-safeband" className="text-accent hover:underline inline-flex items-center gap-1">
                Safe Band Pro <ArrowUpRight size={13} />
              </a>
              , is already in production with a refillable reservoir, breathing valve and
              adjustable band. Extending it into a closed clinical cartridge would be a new
              packaging and sourcing effort built on existing hardware, it has not been tooled,
              manufactured or reviewed for clinical use, and any clinical deployment would still
              require its own regulatory and clinical review, as noted below.
            </p>
          </Reveal>
          <Reveal delay={0.16}>
            <div className="flex items-center gap-3 mt-8">
              <ShieldCheck size={16} className="text-accent" />
              <span className="font-mono text-xs tracking-[0.15em] uppercase text-zinc-400">U.S. Patent No. 12,285,084 B1 &middot; Claim 13</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PARTNERSHIP OPPORTUNITIES */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="clinical-partnership-section">
        <SectionHeading overline="Ways to Work Together" title="Four ways to collaborate." />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-14">
          {PARTNERSHIP_PATHS.map((p, i) => (
            <Reveal key={p.title} delay={Math.min(i * 0.06, 0.3)}>
              <div data-testid={`clinical-partnership-path-${i}`} className="h-full border border-zinc-800 bg-[#0d0d0d] p-7 rounded-xl">
                <p.Icon size={22} className="text-accent" />
                <h3 className="font-heading text-lg font-medium text-white mt-5">{p.title}</h3>
                <p className="font-body text-sm text-zinc-400 leading-relaxed mt-4">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* DISCLAIMER + CTA */}
      <section className="border-t border-zinc-900 bg-[#0a0a0a]" data-testid="clinical-disclaimer-footer">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <div className="border border-zinc-800 bg-[#0d0d0d] rounded-xl p-7">
              <p className="font-body text-sm text-zinc-400 leading-relaxed italic">
                WearSpray is not a cleared medical device, and nothing on this page should be read
                as a clinical claim, a treatment recommendation, or a promise of outcomes for any
                procedure or regimen. Research referenced above comes from published, independently
                cited studies in different patient populations, not a WearSpray-run study, and does
                not establish that WearSpray improves adherence, healing, infection risk or cosmetic
                outcome for any procedure. Clinical deployment of the ideas described here would
                require appropriate regulatory review, professional clinical oversight, and formal
                evaluation before use with patients. This page exists to start a conversation with
                dermatology, medical aesthetics, skincare manufacturing and clinical research
                partners, not to describe a finished or approved product.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap items-center gap-5 mt-10">
              <a
                href="mailto:greg@wearspray.com?subject=Partnership%20Inquiry"
                data-testid="clinical-cta-contact"
                className="inline-flex items-center gap-2 font-body text-sm font-medium bg-accent text-black px-8 py-4 hover:bg-white transition-colors"
              >
                <Mail size={16} /> Start a Partnership Conversation
              </a>
              <a
                href="/docs/WearSpray_Clinical_Recovery_Overview.pdf"
                download
                data-testid="clinical-cta-download-pdf"
                className="inline-flex items-center gap-2 font-body text-sm font-medium border border-zinc-700 text-white px-8 py-4 hover:border-accent hover:text-accent transition-colors"
              >
                <Download size={16} /> Download the One-Pager (PDF)
              </a>
              <Link to="/technology" data-testid="clinical-link-technology" className="font-body text-sm text-zinc-400 hover:text-white transition-colors">
                See the underlying platform
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
